import type { TaskPriority, TaskStatus } from '@pm/schemas';
import { Children, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'violet';

const toneVar: Record<BadgeTone, string> = {
  neutral: 'var(--text-3)',
  accent: 'var(--accent)',
  success: 'var(--success)',
  warning: 'var(--warning)',
  danger: 'var(--danger)',
  info: 'var(--info)',
  violet: 'var(--violet)',
};

/**
 * Centralização óptica do texto, independente da fonte. Cada família reserva espaços diferentes
 * acima e abaixo das letras (ascent/descent), e com altura fixa isso deixava o texto mais alto ou
 * mais baixo conforme a fonte escolhida em Aparência. `text-box: trim-both cap alphabetic` corta a
 * caixa do texto exatamente entre a altura das maiúsculas e a linha de base, com as métricas da
 * própria fonte; o flex então centraliza a tinta. Navegador sem suporte ignora e fica como antes.
 */
const TRIM = '[text-box:trim-both_cap_alphabetic]';

/** Envolve só os trechos de texto (ícones continuam como itens do flex). */
function trimText(children: ReactNode): ReactNode {
  return Children.map(children, (child) => {
    if (typeof child === 'string' || typeof child === 'number') {
      if (typeof child === 'string' && child.trim() === '') return null;
      return <span className={TRIM}>{child}</span>;
    }
    return child;
  });
}

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** soft (padrão): transparente com borda. solid: reservado para URGENT, BLOCKED, atraso e contadores. */
  variant?: 'soft' | 'solid';
  tone?: BadgeTone;
  /** Cor arbitrária (tags do usuário, etapas de workflow). Sobrepõe `tone`. */
  color?: string;
  /** Mono caps (status, prioridade, etapa). Tags de usuário usam sans. */
  mono?: boolean;
  strike?: boolean;
}

export function Badge({
  variant = 'soft',
  tone = 'neutral',
  color,
  mono = true,
  strike,
  className,
  style,
  children,
  ...props
}: BadgeProps) {
  const c = color ?? toneVar[tone];
  const vars = { '--c': c } as CSSProperties;
  return (
    <span
      style={{ ...vars, ...style }}
      className={cn(
        'inline-flex h-5 items-center gap-1 whitespace-nowrap rounded-sm px-1.5 leading-none',
        mono ? 'font-mono text-xs uppercase tracking-[var(--tracking-caps)]' : 'text-sm',
        variant === 'soft'
          ? 'border border-[color-mix(in_oklab,var(--c)_28%,transparent)] bg-[color-mix(in_oklab,var(--c)_12%,transparent)] text-(--c)'
          : 'bg-(--c) text-text-inverse',
        strike && 'line-through',
        className,
      )}
      {...props}
    >
      {trimText(children)}
    </span>
  );
}

/** Tag criada pelo usuário: sans, caixa normal, cor livre. */
export function Tag({ color, className, ...props }: Omit<BadgeProps, 'mono' | 'variant'>) {
  return (
    <Badge
      mono={false}
      variant="soft"
      color={color}
      className={cn('px-[7px]', className)}
      {...props}
    />
  );
}

/** Contador (notificações, itens): solid, arredondado. */
export function Counter({
  tone = 'accent',
  className,
  children,
  ...props
}: Omit<BadgeProps, 'mono' | 'variant'>) {
  return (
    <span
      style={{ '--c': toneVar[tone] } as CSSProperties}
      className={cn(
        'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 font-mono text-[11px] font-medium leading-none',
        tone === 'accent'
          ? 'bg-accent text-accent-fg'
          : tone === 'neutral'
            ? 'bg-surface-3 text-text-2'
            : 'bg-(--c) text-text-inverse',
        className,
      )}
      {...props}
    >
      {trimText(children)}
    </span>
  );
}

const STATUS_LABEL: Record<TaskStatus, string> = {
  NOT_STARTED: 'Não iniciada',
  IN_PROGRESS: 'Em andamento',
  WAITING: 'Aguardando',
  BLOCKED: 'Bloqueada',
  PENDING_APPROVAL: 'Pend. aprovação',
  COMPLETED: 'Concluída',
  CANCELED: 'Cancelada',
};

const STATUS_TONE: Record<TaskStatus, BadgeTone> = {
  NOT_STARTED: 'neutral',
  IN_PROGRESS: 'info',
  WAITING: 'warning',
  BLOCKED: 'danger',
  PENDING_APPROVAL: 'violet',
  COMPLETED: 'success',
  CANCELED: 'neutral',
};

export function StatusBadge({ status, className }: { status: TaskStatus; className?: string }) {
  return (
    <Badge
      tone={STATUS_TONE[status]}
      variant={status === 'BLOCKED' ? 'solid' : 'soft'}
      strike={status === 'CANCELED'}
      className={className}
    >
      {STATUS_LABEL[status]}
    </Badge>
  );
}

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  LOW: 'Baixa',
  NORMAL: 'Normal',
  HIGH: 'Alta',
  URGENT: 'Urgente',
};

/** NORMAL não exibe badge em listas (ruído); use `showNormal` na tela da tarefa. */
export function PriorityBadge({
  priority,
  showNormal,
  className,
}: {
  priority: TaskPriority;
  showNormal?: boolean;
  className?: string;
}) {
  if (priority === 'NORMAL' && !showNormal) return null;
  const tone: BadgeTone =
    priority === 'URGENT'
      ? 'danger'
      : priority === 'HIGH'
        ? 'warning'
        : priority === 'LOW'
          ? 'neutral'
          : 'info';
  return (
    <Badge tone={tone} variant={priority === 'URGENT' ? 'solid' : 'soft'} className={className}>
      {PRIORITY_LABEL[priority]}
    </Badge>
  );
}
